/**
 * Parsing for the upgrade pricing that arrives by postMessage from the One.com
 * parent window, in reply to app-shell's request.
 *
 * The side-menu card's copy quotes a price, so it does not render until a valid
 * message lands — a card reading "From  for the first year" is worse than no
 * card. The modals and the appointment banners quote no price and render
 * regardless.
 *
 * This module is the sole reader of the payload, so a contract change is a
 * one-file edit.
 */

/** Rendered as sent — the parent window owns the formatting. */
export interface UpgradePricing {
  /** First year, e.g. `'$ 4.99 /mo.'`. */
  introductoryPrice: string;
  /** What it renews at, e.g. `'$ 9.99 /mo.'`. */
  renewalPrice: string;
}

/*
 * Deliberately no `planId`. app-shell sends the merchant to One.com's fixed WSB
 * subscription page (`WSB_UPGRADE_URL`), which picks the plan itself, so a plan
 * named in the message would have nowhere to go.
 */

/**
 * Trims an amount, or `null` when it is unusable.
 *
 * Numbers are rejected, not stringified: one would render as a bare amount with
 * no currency.
 */
function normaliseAmount(value: unknown): string | null {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Validates a pricing postMessage payload.
 *
 * A sender quoting only the introductory price is accepted: the renewal price
 * mirrors it, which is what a plan with no introductory discount means.
 *
 * @returns The parsed pricing, or `null` when the payload is unusable.
 */
export function parsePricing(data: unknown): UpgradePricing | null {
  if (!data || typeof data !== 'object') return null;

  const payload = data as Record<string, unknown>;

  const introductoryPrice = normaliseAmount(
    payload.introductoryPrice ?? payload.price
  );
  if (!introductoryPrice) return null;

  const renewalPrice =
    normaliseAmount(payload.renewalPrice) ?? introductoryPrice;

  return { introductoryPrice, renewalPrice };
}
